import { FormattedMessage, defineMessages } from 'react-intl';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import groupBy from 'lodash.groupby';
import Global from '../../store/global-selectors';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { testId } from 'ui-components/services/testid-service';
import { noop } from 'ui-components/default-props/noop';

const messages = defineMessages({
  selectOne: {
    defaultMessage: 'Select One',
    id: '04qg17',
    description: 'dropdown.address.state.selectOne',
  },
});

export const StateDropdown = ({
  componentClassName,
  countryCode = '',
  hasError = false,
  onBlur = noop,
  onChange,
  value = '',
  disabled = false,
  label,
  labelClassName,
}) => {
  const [availableStates, setAvailableStates] = useState([]);
  const [statesByCountry, setStatesByCountry] = useState({});
  const countryData = useSelector(Global.selectReferenceDatagetStates);

  useEffect(() => {
    setStatesByCountry(groupBy(countryData, 'countryCode'));
  }, [countryData]);

  useEffect(() => {
    const states = statesByCountry[countryCode] || [];
    setAvailableStates(states);
  }, [statesByCountry, countryCode]);

  const dropdownTitle = value || <FormattedMessage {...messages.selectOne} />;

  const _onChange = useCallback(
    (event) => {
      const target = event?.target;
      const targetValue = target?.value || '';

      // Case-insensitive matching
      const lowerTargetValue = targetValue.toLowerCase();

      const targetState =
        targetValue.length === 2
          ? availableStates.find((state) => state.code.toLowerCase() === lowerTargetValue)
          : availableStates.find((state) => state.name.toLowerCase() === lowerTargetValue);

      if (targetState) {
        onChange(targetState.code);
        return;
      }
      onChange('');
    },
    [onChange, availableStates],
  );

  return (
    <div className={componentClassName} {...testId('address-state-dropdown')}>
      {label && <label className={labelClassName}>{label}</label>}
      <DropdownMenu disabled={disabled} dropdownMenuShouldHaveWidthOfButton hasError={hasError} value={value} variant='form'>
        <DropdownMenuToggler onBlur={() => onBlur(value)}>
          {/* Hidden input to intercept autofill */}
          <input
            autoComplete='address-level1'
            data-testid='state-autofill-input'
            name='State'
            onChange={_onChange}
            style={{
              position: 'absolute',
              opacity: 0,
              height: '100%',
              width: '100%',
              top: 0,
              left: 0,
            }}
            value={value}
          />
          {dropdownTitle}
        </DropdownMenuToggler>
        <DropdownMenuItem onClick={() => onChange('')}>&nbsp;</DropdownMenuItem>
        {availableStates.map(({ code }) => (
          <DropdownMenuItem key={code} onClick={() => onChange(code)} selected={value === code}>
            {code}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </div>
  );
};

StateDropdown.propTypes = {
  componentClassName: PropTypes.string,
  countryCode: PropTypes.string,
  hasError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.node,
  labelClassName: PropTypes.string,
};
